import { Head, Link, useForm } from '@inertiajs/react'
import { type FormEvent, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface TuneOption {
  id: number
  name: string
}

interface Props {
  heading: string
  title: string
  submitLabel: string
  action: string
  method: 'post' | 'patch'
  tunes: TuneOption[]
  initialName?: string
  initialTuneIds?: number[]
  errors?: Record<string, string | string[]>
}

export default function Form({
  heading,
  title,
  submitLabel,
  action,
  method,
  tunes,
  initialName = '',
  initialTuneIds = [],
  errors,
}: Props) {
  const form = useForm<{ name: string; tune_ids: number[] }>({
    name: initialName,
    tune_ids: initialTuneIds,
  })
  const [query, setQuery] = useState('')

  const byId = useMemo(() => new Map(tunes.map((t) => [t.id, t])), [tunes])

  const nameError = errors?.name
  const showNameError = Array.isArray(nameError) ? nameError.join(', ') : nameError

  const selectedIds = form.data.tune_ids
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    return tunes
      .filter((t) => !selectedIds.includes(t.id))
      .filter((t) => (q ? t.name.toLowerCase().includes(q) : true))
      .slice(0, 8)
  }, [tunes, selectedIds, query])

  function addTune(id: number) {
    form.setData('tune_ids', [...selectedIds, id])
    setQuery('')
  }

  function removeTune(id: number) {
    form.setData(
      'tune_ids',
      selectedIds.filter((tid) => tid !== id),
    )
  }

  function move(index: number, delta: number) {
    const target = index + delta
    if (target < 0 || target >= selectedIds.length) return
    const next = [...selectedIds]
    ;[next[index], next[target]] = [next[target], next[index]]
    form.setData('tune_ids', next)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    form.submit(method, action)
  }

  return (
    <>
      <Head title={title} />

      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-2xl font-semibold tracking-tight">{heading}</h1>
        <Button variant="ghost" asChild>
          <Link href="/sets">Cancel</Link>
        </Button>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Name</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="name" className="sr-only">
                Name
              </Label>
              <Input
                id="name"
                placeholder="The Bucks of Oranmore set"
                value={form.data.name}
                onChange={(e) => form.setData('name', e.target.value)}
              />
              {showNameError && <p className="text-xs text-destructive">{showNameError}</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tunes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedIds.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tunes added yet.</p>
            ) : (
              <ol className="space-y-2">
                {selectedIds.map((id, index) => (
                  <li key={id} className="flex items-center gap-2 rounded-md border px-3 py-2">
                    <span className="w-5 text-sm text-muted-foreground">{index + 1}.</span>
                    <span className="font-display font-medium">{byId.get(id)?.name}</span>
                    <div className="ml-auto flex gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                      >
                        ↑
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={index === selectedIds.length - 1}
                        onClick={() => move(index, 1)}
                      >
                        ↓
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeTune(id)}
                      >
                        Remove
                      </Button>
                    </div>
                  </li>
                ))}
              </ol>
            )}

            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="tune-search">Add a tune</Label>
              <Input
                id="tune-search"
                placeholder="Search the book…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query.trim() && (
                <ul className="rounded-md border">
                  {matches.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-muted-foreground">No matches.</li>
                  ) : (
                    matches.map((t) => (
                      <li key={t.id}>
                        <button
                          type="button"
                          onClick={() => addTune(t.id)}
                          className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-accent"
                        >
                          {t.name}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>

        <Button type="submit" disabled={form.processing || !form.data.name.trim()}>
          {submitLabel}
        </Button>
      </form>
    </>
  )
}
